import type { Icon } from "@phosphor-icons/react";
import { AddressBookIcon } from "@phosphor-icons/react/dist/csr/AddressBook";
import { BankIcon } from "@phosphor-icons/react/dist/csr/Bank";
import { BuildingsIcon } from "@phosphor-icons/react/dist/csr/Buildings";
import { ChatTextIcon } from "@phosphor-icons/react/dist/csr/ChatText";
import { ClockCounterClockwiseIcon } from "@phosphor-icons/react/dist/csr/ClockCounterClockwise";
import { IdentificationCardIcon } from "@phosphor-icons/react/dist/csr/IdentificationCard";
import { KeyIcon } from "@phosphor-icons/react/dist/csr/Key";
import { ShareNetworkIcon } from "@phosphor-icons/react/dist/csr/ShareNetwork";
import { StarIcon } from "@phosphor-icons/react/dist/csr/Star";
import { UsersIcon } from "@phosphor-icons/react/dist/csr/Users";
import { useRef } from "react";
import { useLocation } from "react-router-dom";
import { getProductCapabilities } from "@/business/product-capabilities";
import { useGetBusinessReviewAccess } from "@/business/query";
import {
  BUSINESS_SETTINGS_ROUTES,
  SIDEBAR_MODES,
  SIDEBAR_SECTIONS,
} from "@/business-settings/constants";
import { useBusinessSettingsSidebar } from "@/business-settings/context/business-settings-sidebar-context";
import { useUsersWorkbookAccess } from "@/business-settings/users/workbook-access/use-users-workbook-access";
import { useBusiness } from "@/core/business";
import { useBusinessProducts } from "@/core/business/use-business-products";
import {
  type PayrollSettingsAccessPolicy,
  type PayrollSettingsPageId,
} from "@/payroll/settings/access-policy";
import { PAYROLL_SETTINGS_PATH } from "@/payroll/settings/routes";
import { PAYROLL_SETTINGS_ACCESS_PATH } from "@/payroll/settings/routes";
import { useUserManagementAccess } from "@/user/management-access";
import { useGetMyUser } from "@/user/query";
import { isAdminOrOwnerOrManager } from "@/user/utils";
import { isPathUnder } from "./nav-items";
import { appPathToRemember } from "./sidebar-return";

export type SettingsGroup = "Business" | "Referrals" | "Payroll";

export interface SettingsNavItem {
  title: string;
  href: string;
  isActive: boolean;
  icon: Icon;
  group: SettingsGroup;
}

export interface SettingsNav {
  href?: string;
  items: SettingsNavItem[];
  isActive: boolean;
  returnTo: string;
  onNavigate: () => void;
}

const PAYROLL_SETTINGS_PAGES: Record<
  PayrollSettingsPageId,
  { title: string; icon: Icon }
> = {
  roster: { title: "Roster", icon: IdentificationCardIcon },
  "workbook-access": { title: "Workbook access", icon: KeyIcon },
};

export const useSettingsNav = (
  payrollSettings: PayrollSettingsAccessPolicy,
): SettingsNav => {
  const { pathname } = useLocation();
  const { businessId } = useBusiness();
  const { data: user } = useGetMyUser();
  const { canManageUsers, canManageWorkbookAccess } =
    useUserManagementAccess(businessId);
  const { settings } = useBusinessProducts();
  const workbookAccess = useUsersWorkbookAccess(businessId);
  const enableReferrals = getProductCapabilities({ user, settings }).enabled
    .referrals;
  const { updateSidebar } = useBusinessSettingsSidebar();
  const { data: reviewAccess } = useGetBusinessReviewAccess(
    enableReferrals ? (businessId ?? undefined) : undefined,
  );

  const referrals = enableReferrals;
  const businessPath = `/business-settings/${businessId}`;
  const businessItems: {
    title: string;
    route: string;
    show: boolean;
    icon: Icon;
    group: SettingsGroup;
  }[] = [
    {
      title: "Business profile",
      route: BUSINESS_SETTINGS_ROUTES.business,
      show: enableReferrals,
      icon: BuildingsIcon,
      group: "Business",
    },
    {
      title: "Payments",
      route: BUSINESS_SETTINGS_ROUTES.payments,
      show: referrals && isAdminOrOwnerOrManager(user?.role),
      icon: BankIcon,
      group: "Business",
    },
    {
      title: "Users",
      route: BUSINESS_SETTINGS_ROUTES.users,
      show: canManageUsers || canManageWorkbookAccess,
      icon: UsersIcon,
      group: "Business",
    },
    {
      title: "Customers",
      route: BUSINESS_SETTINGS_ROUTES.customers,
      show: referrals,
      icon: AddressBookIcon,
      group: "Referrals",
    },
    {
      title: "Referral history",
      route: BUSINESS_SETTINGS_ROUTES.referralHistory,
      show: referrals,
      icon: ClockCounterClockwiseIcon,
      group: "Referrals",
    },
    {
      title: "Referral sources",
      route: BUSINESS_SETTINGS_ROUTES.referralSources,
      show: referrals,
      icon: ShareNetworkIcon,
      group: "Referrals",
    },
    {
      title: "Text messages",
      route: BUSINESS_SETTINGS_ROUTES.textMessages,
      show: referrals,
      icon: ChatTextIcon,
      group: "Referrals",
    },
    {
      title: "Five-star reviews",
      route: BUSINESS_SETTINGS_ROUTES.reviews,
      show: referrals && (reviewAccess?.has_review_access ?? false),
      icon: StarIcon,
      group: "Referrals",
    },
  ];

  const items: SettingsNavItem[] = [
    ...(businessId
      ? businessItems
          .filter((item) => item.show)
          .map(({ title, route, icon, group }) => {
            const href =
              route === "/" ? `${businessPath}/` : businessPath + route;
            return {
              title,
              href,
              icon,
              group,
              isActive:
                route === "/"
                  ? pathname === businessPath || pathname === `${businessPath}/`
                  : isPathUnder(pathname, href),
            };
          })
      : []),
    ...(workbookAccess.settings
      ? [
          {
            title: "Workbook access",
            href: `${PAYROLL_SETTINGS_ACCESS_PATH}?business=${businessId}`,
            icon: KeyIcon,
            group: "Payroll" as const,
            isActive:
              isPathUnder(pathname, PAYROLL_SETTINGS_ACCESS_PATH) ||
              (pathname === PAYROLL_SETTINGS_PATH &&
                payrollSettings.landing === PAYROLL_SETTINGS_ACCESS_PATH),
          },
        ]
      : []),
    ...payrollSettings.pages
      .filter((page) => page.id !== "workbook-access")
      .map((page) => ({
        title: PAYROLL_SETTINGS_PAGES[page.id].title,
        href: page.path,
        icon: PAYROLL_SETTINGS_PAGES[page.id].icon,
        group: "Payroll" as const,
        isActive:
          (pathname === PAYROLL_SETTINGS_PATH &&
            page.path === payrollSettings.landing) ||
          isPathUnder(pathname, page.path),
      })),
  ];

  const isActive =
    isPathUnder(pathname, "/business-settings") ||
    isPathUnder(pathname, PAYROLL_SETTINGS_PATH);
  const returnTo = useRef("/");
  returnTo.current = appPathToRemember(pathname, returnTo.current);

  return {
    href: items[0]?.href,
    items,
    isActive,
    returnTo: returnTo.current,
    onNavigate: () =>
      updateSidebar({
        isOpen: false,
        mode: SIDEBAR_MODES.view,
        section: SIDEBAR_SECTIONS.users,
        selectedItemId: undefined,
      }),
  };
};
